import { gzipSync } from "fflate";

/** Build a gzip'd USTAR tarball from in-memory files. Deterministic; the backend
 *  reads it with tarfile r:gz and expects plugin.yaml at the archive root. */

function octal(n: number, len: number): string {
  // len includes the trailing NUL; value is (len-1) octal digits, zero-padded.
  return n.toString(8).padStart(len - 1, "0") + "\0";
}

function tarHeader(path: string, size: number): Uint8Array {
  const h = new Uint8Array(512);
  const enc = new TextEncoder();
  const nameBytes = enc.encode(path);
  if (nameBytes.length > 100) throw new Error(`path too long for USTAR: ${path}`);
  h.set(nameBytes, 0);                                    // name (100)
  h.set(enc.encode("0000644\0"), 100);                    // mode
  h.set(enc.encode("0000000\0"), 108);                    // uid
  h.set(enc.encode("0000000\0"), 116);                    // gid
  h.set(enc.encode(octal(size, 12)), 124);                // size (12)
  h.set(enc.encode("00000000000\0"), 136);                // mtime (0)
  h.set(enc.encode("        "), 148);                     // checksum placeholder (8 spaces)
  h[156] = "0".charCodeAt(0);                             // typeflag = regular file
  h.set(enc.encode("ustar\0"), 257);                      // magic
  h.set(enc.encode("00"), 263);                           // version
  // checksum = sum of all header bytes with the checksum field as spaces
  let sum = 0;
  for (let i = 0; i < 512; i++) sum += h[i];
  h.set(enc.encode(sum.toString(8).padStart(6, "0") + "\0 "), 148);
  return h;
}

export function buildTarball(files: { path: string; bytes: Uint8Array }[]): Uint8Array {
  const blocks: Uint8Array[] = [];
  for (const f of files) {
    blocks.push(tarHeader(f.path, f.bytes.length));
    blocks.push(f.bytes);
    const pad = (512 - (f.bytes.length % 512)) % 512;
    if (pad) blocks.push(new Uint8Array(pad));
  }
  blocks.push(new Uint8Array(1024)); // two zero blocks = end of archive
  const total = blocks.reduce((n, b) => n + b.length, 0);
  const tar = new Uint8Array(total);
  let off = 0;
  for (const b of blocks) { tar.set(b, off); off += b.length; }
  return gzipSync(tar);
}
